import {
  ACCURACY_BUILD_S,
  ACCURACY_RECOVER_S,
  AIM_TIMEOUT_MS,
  AIM_TURN_SPEED,
  BULLET_SPEED,
  MANEUVER_LERP_SPEED,
  MANEUVER_SECONDS,
  MANEUVER_STEP,
  MAX_SPREAD_HALF_RAD,
  SETTLE_MS,
  STATE_SYNC_MS,
  TURN_SPREAD_EPS,
  clampToOwnHalf,
  onGuestHalf,
  onHostHalf,
  pointInArena,
  segmentHitAabb,
  segmentHitHitBox,
  type DuelOutcome,
  type PeerMessage,
  type Role,
} from "@duelgame/shared";
import type { PeerLink } from "../net/peerLink";
import {
  createInitialDuelState,
  fighterFor,
  lerpDrawPose,
  otherRole,
  syncDrawPose,
  type Bullet,
  type DuelState,
  type Fighter,
} from "./duel";
import { DECOR_SIZE, generateDecor, isNightArena } from "./decor";
import { DustField } from "./dust";
import { NightMistField } from "./nightMist";
import { DEAD_W, getSprite } from "./sprites";
import {
  ABILITY_CACTUS_SUIT,
  ABILITY_COWBOY_DUMMY,
  ABILITY_DOUBLE_PACE,
  ABILITY_FLASHBANG,
  ABILITY_SHOTGUN,
  ABILITY_SNEAK_PEEK,
  DOUBLE_PACE_LERP_SPEED,
  FLASHBANG_FLIGHT_MS,
  SHOTGUN_PELLETS,
  SHOTGUN_SPEED,
  SNEAK_PEEK_FADE_MS,
  SNEAK_PEEK_MS,
} from "./abilities";
import {
  flashbangTarget,
  type FlashbangThrow,
} from "./flashbang";
import { FEET_OFFSET_Y, STEP_FRAMES } from "./sprites";
import { TumbleweedField } from "./tumbleweed";
import { JackrabbitField } from "./jackrabbit";
import { maybeRollSnake, SNAKE_FRAMES, SnakeField } from "./snake";
import {
  EMOTE_COOLDOWN_MS,
  EMOTE_DURATION_MS,
  randomEmoteLine,
} from "./emotes";
import {
  playBang,
  playBell,
  playCountdown,
  stopCountdown,
  playFlashbang,
  playFootstep,
  playHiss,
  playHurt,
  playInflate,
  playPop,
  playResultTheme,
  playWhip,
  stopMenuMusic,
  stopResultTheme,
} from "./audio";

let bulletId = 1;

export class DuelController {
  state: DuelState = createInitialDuelState();
  private role: Role;
  private session: PeerLink;
  private resolved = false;
  private unsub: (() => void) | null = null;
  private lastTickShown: number | null = null;
  private lastSyncMs = 0;
  /** Peer pressed Rematch while we were still on the result screen. */
  private remoteRematchReady = false;
  private lastEmoteMs = 0;
  /** Local-only dust puffs at the feet while pacing. */
  readonly dust = new DustField();
  /** Local-only dusk mist for nighttime rounds. */
  readonly nightMist = new NightMistField();
  /** Host-authoritative tumbleweeds; guest predicts between state snaps. */
  readonly tumbleweeds = new TumbleweedField();
  /** Host-authoritative jackrabbits; guest predicts between state snaps. */
  readonly jackrabbits = new JackrabbitField();
  /** Host-authoritative corner snake; guest snaps from sync. */
  readonly snake = new SnakeField();
  private lastDustFrame = new Map<Fighter, number>();
  /** Abilities chosen on the ready screen (local only for now). */
  private localAbilities: number[] = [];
  /** Opponent's loadout (synced on ready). */
  private remoteAbilities: number[] = [];
  private localDummyPlaced = false;
  private sneakPeekFired = false;
  private sneakPeekStartMs: number | null = null;
  /** Queued paces still to take this countdown second (Double Pace = 2). */
  private pendingPaces = 0;
  /** Active flashbang throws this round (local sim from synced loadouts). */
  private flashbangs: FlashbangThrow[] = [];
  private flashbangSoundPlayed = false;

  constructor(
    session: PeerLink,
    role: Role,
    private readonly allowRandomEvents = true
  ) {
    this.session = session;
    this.role = role;
    this.unsub = session.onPeerMessage((msg) => this.onPeer(msg));
  }

  destroy(): void {
    this.unsub?.();
    this.unsub = null;
  }

  /**
   * Push match clocks forward by `deltaMs` so a tutorial pause doesn't burn
   * settle / maneuver / aim time.
   */
  shiftClocks(deltaMs: number): void {
    if (deltaMs <= 0) return;
    const s = this.state;
    if (s.matchStartMs !== null) s.matchStartMs += deltaMs;
    if (s.settleEndMs !== null) s.settleEndMs += deltaMs;
    if (s.maneuverEndMs !== null) s.maneuverEndMs += deltaMs;
    if (s.aimEndMs !== null) s.aimEndMs += deltaMs;
    if (this.sneakPeekStartMs !== null) this.sneakPeekStartMs += deltaMs;
    this.lastSyncMs += deltaMs;
    this.lastEmoteMs += deltaMs;
    for (const fb of this.flashbangs) fb.startMs += deltaMs;
    for (const d of s.dummies) d.placedAtMs += deltaMs;
    for (const f of [s.host, s.guest]) {
      if (f.bubbleUntilMs > 0) f.bubbleUntilMs += deltaMs;
    }
    if (s.horseBubbleUntilMs > 0) s.horseBubbleUntilMs += deltaMs;
  }

  resetForRematch(): void {
    this.state = createInitialDuelState();
    this.resolved = false;
    this.lastTickShown = null;
    this.lastSyncMs = 0;
    this.dust.clear();
    this.nightMist.clear();
    this.lastDustFrame.clear();
    this.tumbleweeds.reset();
    this.jackrabbits.reset(this.allowRandomEvents);
    this.snake.clear();
    this.sneakPeekFired = false;
    this.sneakPeekStartMs = null;
    this.remoteAbilities = [];
    this.pendingPaces = 0;
    this.flashbangs = [];
    this.flashbangSoundPlayed = false;
    stopResultTheme();
    stopCountdown();
    // localAbilities kept — setLocalAbilities runs before rematch ready
  }

  setLocalAbilities(ids: number[]): void {
    this.localAbilities = [...ids];
  }

  hasAbility(id: number): boolean {
    return this.localAbilities.includes(id);
  }

  peerHasAbility(id: number): boolean {
    return this.remoteAbilities.includes(id);
  }

  hasPlacedCowboyDummy(): boolean {
    return this.localDummyPlaced;
  }

  getFlashbangs(): readonly FlashbangThrow[] {
    return this.flashbangs;
  }

  /** Cactus Suit kicks in when the pace countdown starts. */
  wearsCactusSuit(isLocal: boolean): boolean {
    const phase = this.state.phase;
    if (phase !== "maneuver" && phase !== "aim" && phase !== "result") {
      return false;
    }
    const ids = isLocal ? this.localAbilities : this.remoteAbilities;
    return ids.includes(ABILITY_CACTUS_SUIT);
  }

  /**
   * Fog clear amount for Sneak Peek (0 = full fog, 1 = fully clear).
   * Quick fade in/out, long fully-clear middle.
   */
  getSneakPeekReveal(now = performance.now()): number {
    if (this.sneakPeekStartMs === null) return 0;
    const elapsed = now - this.sneakPeekStartMs;
    if (elapsed <= 0 || elapsed >= SNEAK_PEEK_MS) return 0;
    const fade = SNEAK_PEEK_FADE_MS;
    if (elapsed < fade) return elapsed / fade;
    if (elapsed > SNEAK_PEEK_MS - fade) {
      return (SNEAK_PEEK_MS - elapsed) / fade;
    }
    return 1;
  }

  setLocalReady(ready: boolean): void {
    this.state.localReady = ready;
    this.session.sendPeer({
      type: "ready",
      ready,
      abilities: ready ? this.localAbilities : undefined,
    });
    this.maybeStartMatch();
  }

  setMouse(x: number, y: number): void {
    this.state.mouseX = x;
    this.state.mouseY = y;
  }

  onArenaClick(_x: number, _y: number): void {
    if (this.state.phase === "aim") {
      this.tryShoot();
    }
  }

  tryShoot(): void {
    if (this.resolved || this.state.phase !== "aim") return;
    const me = fighterFor(this.state, this.role);
    if (me.hasShot || me.hit) return;

    me.hasShot = true;
    me.targetX = null;
    me.targetY = null;
    me.moveToX = null;
    me.moveToY = null;

    const shotgun = this.hasAbility(ABILITY_SHOTGUN);
    if (shotgun) {
      const pellets: number[] = [];
      for (let i = 0; i < SHOTGUN_PELLETS; i++) {
        pellets.push(me.facing + (Math.random() * 2 - 1) * me.spreadHalf);
      }
      for (let i = 0; i < pellets.length; i++) {
        this.spawnBullet(this.role, me.x, me.y, pellets[i]!, {
          shotgun: true,
          smoke: i === 0,
        });
      }
      playBang();
      this.session.sendPeer({
        type: "shoot",
        x: me.x,
        y: me.y,
        facing: me.facing,
        pellets,
      });
    } else {
      const fireFacing =
        me.facing + (Math.random() * 2 - 1) * me.spreadHalf;
      this.spawnBullet(this.role, me.x, me.y, fireFacing);
      playBang();
      this.session.sendPeer({
        type: "shoot",
        x: me.x,
        y: me.y,
        facing: fireFacing,
      });
    }
    this.maybeResolve();
  }

  /** Press T — random speech bubble above your head, synced to peer. */
  tryEmote(now = performance.now()): void {
    if (!this.state.localReady) return;
    if (now - this.lastEmoteMs < EMOTE_COOLDOWN_MS) return;
    this.lastEmoteMs = now;
    const text = randomEmoteLine();
    this.showBubble(fighterFor(this.state, this.role), text, now);
    this.session.sendPeer({ type: "emote", text });
  }

  /** Space — drop Cowboy Dummy at your feet during the countdown (once). */
  tryPlaceDummy(now = performance.now()): void {
    if (this.state.phase !== "maneuver") return;
    if (!this.hasAbility(ABILITY_COWBOY_DUMMY)) return;
    if (this.localDummyPlaced) return;

    const me = fighterFor(this.state, this.role);
    // Always face the opponent's half (host→right, guest→left)
    const facingLeft = this.role === "guest";
    const dummy = {
      owner: this.role,
      x: me.x,
      y: me.y,
      facingLeft,
      placedAtMs: now,
    };
    this.state.dummies.push(dummy);
    this.localDummyPlaced = true;
    playInflate();
    this.session.sendPeer({
      type: "dummy",
      x: dummy.x,
      y: dummy.y,
      facingLeft: dummy.facingLeft,
    });
  }

  tick(now: number, dtMs: number): void {
    this.updatePhases(now);
    this.updateManeuverMovement(dtMs / 1000);
    this.updateAimFacing(dtMs / 1000);
    this.updateBullets(dtMs / 1000);
    this.clearExpiredBubbles(now);
    this.updateDrawPoses(dtMs / 1000);
    this.updateDust(dtMs / 1000);
    this.tumbleweeds.update(dtMs / 1000);
    this.jackrabbits.update(dtMs / 1000);
    this.updateSnake(dtMs / 1000);
    if (this.state.isNight) this.nightMist.update(dtMs / 1000);
    this.maybePlayFlashbangSound(now);
    this.syncState(now);
  }

  /** Kick up a puff each time a fighter's step strip advances a frame. */
  private updateDust(dt: number): void {
    if (this.state.phase === "maneuver") {
      for (const f of [this.state.host, this.state.guest]) {
        const stepping = f.drawStepProgress > 0 && f.drawStepCount > 0;
        if (!stepping || f.hit) continue;
        const frame = Math.min(
          STEP_FRAMES - 1,
          Math.floor(f.drawStepProgress * STEP_FRAMES)
        );
        const key = f.drawStepCount * STEP_FRAMES + frame;
        if (this.lastDustFrame.get(f) !== key) {
          this.lastDustFrame.set(f, key);
          this.dust.spawnStepPuff(f.drawX, f.drawY, f.drawFacingLeft ? -1 : 1);
        }
      }
    }
    this.dust.update(dt);
  }

  private showBubble(f: Fighter, text: string, now: number): void {
    f.bubbleText = text;
    f.bubbleUntilMs = now + EMOTE_DURATION_MS;
    const freezeAtEnemyPosition =
      this.state.fogActive && f !== fighterFor(this.state, this.role);
    f.bubbleX = freezeAtEnemyPosition ? f.drawX : null;
    f.bubbleY = freezeAtEnemyPosition ? f.drawY : null;
  }

  private clearExpiredBubbles(now: number): void {
    for (const f of [this.state.host, this.state.guest]) {
      if (f.bubbleText !== null && now >= f.bubbleUntilMs) {
        f.bubbleText = null;
        f.bubbleUntilMs = 0;
        f.bubbleX = null;
        f.bubbleY = null;
      }
    }
    if (
      this.state.horseBubbleText !== null &&
      now >= this.state.horseBubbleUntilMs
    ) {
      this.state.horseBubbleText = null;
      this.state.horseBubbleUntilMs = 0;
    }
  }

  private clearAllBubbles(): void {
    for (const f of [this.state.host, this.state.guest]) {
      f.bubbleText = null;
      f.bubbleUntilMs = 0;
      f.bubbleX = null;
      f.bubbleY = null;
    }
    this.state.horseBubbleText = null;
    this.state.horseBubbleUntilMs = 0;
  }

  private triggerHorseBubble(now: number): void {
    const horse = this.state.decor.find((p) => p.type === "horse");
    if (!horse) return;
    this.state.horseBubbleText = "A horse is a horse, of course, of course.";
    this.state.horseBubbleUntilMs = now + EMOTE_DURATION_MS + 1000;
    this.state.horseBubbleX = horse.x;
    this.state.horseBubbleY = horse.y;
  }

  private maybeStartMatch(): void {
    if (this.role !== "host") return;
    if (!this.state.localReady || !this.state.remoteReady) return;
    if (this.state.phase !== "waiting") return;

    const hostStartMs = performance.now();
    const decorSeed = (Math.random() * 0xffffffff) >>> 0;
    const isNight = this.allowRandomEvents && isNightArena(decorSeed);
    const snakeCorner = this.allowRandomEvents
      ? maybeRollSnake(decorSeed)
      : null;
    this.applyMatchStart(
      hostStartMs,
      hostStartMs,
      SETTLE_MS,
      MANEUVER_SECONDS * 1000,
      AIM_TIMEOUT_MS,
      decorSeed,
      isNight,
      snakeCorner
    );
    this.session.sendPeer({
      type: "match_start",
      hostStartMs,
      settleMs: SETTLE_MS,
      maneuverMs: MANEUVER_SECONDS * 1000,
      aimMs: AIM_TIMEOUT_MS,
      decorSeed,
      isNight,
      snakeCorner,
    });
  }

  private applyMatchStart(
    localOrigin: number,
    _hostStartMs: number,
    settleMs: number,
    maneuverMs: number,
    aimMs: number,
    decorSeed: number,
    isNight: boolean,
    snakeCorner: number | null
  ): void {
    this.state.matchStartMs = localOrigin;
    this.state.settleEndMs = localOrigin + settleMs;
    this.state.maneuverEndMs = localOrigin + settleMs + maneuverMs;
    this.state.aimEndMs = localOrigin + settleMs + maneuverMs + aimMs;
    this.state.phase = "settle";
    this.state.fogActive = false;
    this.state.countdownDisplay = null;
    this.lastTickShown = null;
    this.state.decor = generateDecor(decorSeed, this.allowRandomEvents);
    this.state.isNight = isNight;
    this.nightMist.clear();
    if (isNight) this.nightMist.activate();
    this.snake.spawn(snakeCorner);
    this.state.horseBubbleText = null;
    this.state.horseBubbleUntilMs = 0;
    this.state.dummies = [];
    this.localDummyPlaced = false;
    this.flashbangs = [];
    this.flashbangSoundPlayed = false;
    this.tumbleweeds.reset();
    this.jackrabbits.reset(this.allowRandomEvents);
    this.sneakPeekFired = false;
    this.sneakPeekStartMs = null;
    this.pendingPaces = 0;
    stopMenuMusic();
  }

  /** Dust burst when anyone with Cactus Suit swaps into costume. */
  private triggerCactusSuitTransforms(): void {
    if (this.hasAbility(ABILITY_CACTUS_SUIT)) {
      const me = fighterFor(this.state, this.role);
      this.dust.spawnCostumePuff(me.drawX, me.drawY);
    }
    if (this.peerHasAbility(ABILITY_CACTUS_SUIT)) {
      const them = fighterFor(this.state, otherRole(this.role));
      this.dust.spawnCostumePuff(them.drawX, them.drawY);
    }
  }

  /** Kick off flashbang throws for anyone who picked the ability. */
  private startFlashbangs(now: number): void {
    this.flashbangs = [];
    this.flashbangSoundPlayed = false;
    const spawn = (thrower: Role) => {
      const f = fighterFor(this.state, thrower);
      const target = flashbangTarget(thrower);
      this.flashbangs.push({
        thrower,
        startMs: now,
        fromX: f.x,
        fromY: f.y,
        toX: target.x,
        toY: target.y,
      });
    };
    if (this.hasAbility(ABILITY_FLASHBANG)) spawn(this.role);
    if (this.peerHasAbility(ABILITY_FLASHBANG)) spawn(otherRole(this.role));
  }

  /** Detonation SFX when the white-out begins. */
  private maybePlayFlashbangSound(now: number): void {
    if (this.flashbangSoundPlayed || this.flashbangs.length === 0) return;
    const live = this.flashbangs.filter(
      (fb) => now - fb.startMs >= FLASHBANG_FLIGHT_MS
    );
    if (live.length === 0) return;
    this.flashbangSoundPlayed = true;

    if (this.flashbangs.length >= 2) {
      // Both threw — one full-volume bang for everyone
      playFlashbang(1);
      return;
    }
    const fb = live[0]!;
    playFlashbang(fb.thrower === this.role ? 0.25 : 1);
  }

  private updatePhases(now: number): void {
    if (this.state.phase === "waiting" || this.state.phase === "result") return;
    if (!this.state.settleEndMs || !this.state.maneuverEndMs || !this.state.aimEndMs) return;

    if (this.state.phase === "settle" && now >= this.state.settleEndMs) {
      this.state.phase = "maneuver";
      this.state.fogActive = true;
      this.clearAllBubbles();
      this.turnBacksToEachOther();
      playBell(); // bell kicks off the pace-off
      playCountdown(); // full tick track (all seconds in one file)
      this.triggerCactusSuitTransforms();
    }

    if (this.state.phase === "maneuver") {
      const remaining = Math.ceil((this.state.maneuverEndMs - now) / 1000);
      const display = Math.max(0, remaining);

      // Halfway through the countdown — one-shot Sneak Peek
      if (
        !this.sneakPeekFired &&
        this.hasAbility(ABILITY_SNEAK_PEEK) &&
        this.state.settleEndMs !== null
      ) {
        const halfAt =
          this.state.settleEndMs +
          (this.state.maneuverEndMs - this.state.settleEndMs) / 2;
        if (now >= halfAt) {
          this.sneakPeekFired = true;
          this.sneakPeekStartMs = now;
        }
      }

      if (display !== this.lastTickShown) {
        // A countdown second just ended — pace toward the mouse
        if (this.lastTickShown !== null && display < this.lastTickShown) {
          this.queuePaces(this.hasAbility(ABILITY_DOUBLE_PACE) ? 2 : 1);
        }
        this.lastTickShown = display;
      }
      this.state.countdownDisplay = display > 0 ? display : 0;

      if (now >= this.state.maneuverEndMs) {
        this.state.phase = "aim";
        stopCountdown();
        this.state.fogActive = false;
        this.state.countdownDisplay = null;
        const me = fighterFor(this.state, this.role);
        me.targetX = null;
        me.targetY = null;
        me.moveToX = null;
        me.moveToY = null;
        me.stepProgress = 0;
        me.stepDist = 0;
        this.pendingPaces = 0;
        this.state.host.spreadHalf = MAX_SPREAD_HALF_RAD;
        this.state.guest.spreadHalf = MAX_SPREAD_HALF_RAD;
        this.state.host.drawSpreadHalf = MAX_SPREAD_HALF_RAD;
        this.state.guest.drawSpreadHalf = MAX_SPREAD_HALF_RAD;
        this.startFlashbangs(now);
        playWhip(); // whip crack as the countdown ends / draw
        this.triggerHorseBubble(now);
      }
    }

    if (this.state.phase === "aim" && now >= this.state.aimEndMs) {
      this.maybeResolve(true);
    }
  }

  /** Face away from center so duellists start the pace-off back-to-back. */
  private turnBacksToEachOther(): void {
    this.state.host.facing = Math.PI;
    this.state.host.facingLeft = true;
    this.state.host.drawFacing = Math.PI;
    this.state.host.drawFacingLeft = true;
    this.state.guest.facing = 0;
    this.state.guest.facingLeft = false;
    this.state.guest.drawFacing = 0;
    this.state.guest.drawFacingLeft = false;
  }

  /** Lerp toward the current step destination and refresh the next-pace marker. */
  private updateManeuverMovement(dt: number): void {
    if (this.state.phase !== "maneuver") return;
    const me = fighterFor(this.state, this.role);
    const doublePace = this.hasAbility(ABILITY_DOUBLE_PACE);
    const lerpSpeed = doublePace ? DOUBLE_PACE_LERP_SPEED : MANEUVER_LERP_SPEED;

    if (me.moveToX !== null && me.moveToY !== null) {
      const dx = me.moveToX - me.x;
      const dy = me.moveToY - me.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 1) {
        me.x = me.moveToX;
        me.y = me.moveToY;
        me.moveToX = null;
        me.moveToY = null;
        me.stepProgress = 0;
        me.stepDist = 0;
        // Chain the second Double Pace step as soon as the first lands
        this.startNextPace();
      } else {
        // Arrive well before the next countdown tick
        const step = lerpSpeed * dt;
        const t = Math.min(1, step / dist);
        me.x += dx * t;
        me.y += dy * t;
        me.facing = Math.atan2(dy, dx);
        me.facingLeft = dx < 0;
        if (me.stepDist > 0) {
          me.stepProgress = Math.min(1, 1 - dist / me.stepDist);
        }
      }
    } else {
      this.startNextPace();
    }

    const next = this.nextStepPose(me.x, me.y, me.facing);
    me.targetX = next.x;
    me.targetY = next.y;
  }

  private queuePaces(count: number): void {
    this.pendingPaces += count;
    this.startNextPace();
  }

  private startNextPace(): void {
    if (this.pendingPaces <= 0) return;
    const me = fighterFor(this.state, this.role);
    if (me.moveToX !== null && me.moveToY !== null) return;
    this.pendingPaces -= 1;
    this.takeManeuverStep();
  }

  private takeManeuverStep(): void {
    const me = fighterFor(this.state, this.role);
    const next = this.nextStepPose(me.x, me.y, me.facing);
    me.moveToX = next.x;
    me.moveToY = next.y;
    me.facing = next.facing;
    me.facingLeft = Math.cos(next.facing) < 0;
    me.targetX = next.x;
    me.targetY = next.y;
    me.stepCount += 1;
    me.stepDist = Math.hypot(next.x - me.x, next.y - me.y);
    me.stepProgress = me.stepDist > 0 ? 0.001 : 0;
    if (me.stepDist > 0) playFootstep(me.stepCount);
  }

  private nextStepPose(
    fromX: number,
    fromY: number,
    currentFacing: number
  ): { x: number; y: number; facing: number } {
    // Aim from the feet so the pace arrow matches the cursor
    const feetX = fromX;
    const feetY = fromY + FEET_OFFSET_Y;
    const dx = this.state.mouseX - feetX;
    const dy = this.state.mouseY - feetY;
    const dist = Math.hypot(dx, dy);
    let facing = currentFacing;
    let x = fromX;
    let y = fromY;
    if (dist > 1) {
      facing = Math.atan2(dy, dx);
      x = fromX + (dx / dist) * MANEUVER_STEP;
      y = fromY + (dy / dist) * MANEUVER_STEP;
    }
    const clamped = clampToOwnHalf(this.role, x, y);
    return { x: clamped.x, y: clamped.y, facing };
  }

  private updateAimFacing(dt: number): void {
    if (this.state.phase !== "aim") return;
    const me = fighterFor(this.state, this.role);
    if (me.hasShot || me.hit) return;

    const desired = Math.atan2(
      this.state.mouseY - me.y,
      this.state.mouseX - me.x
    );
    const before = me.facing;
    me.facing = turnToward(me.facing, desired, AIM_TURN_SPEED * dt);
    me.facingLeft = this.state.mouseX < me.x;
    const turned = Math.abs(angleDiff(before, me.facing));
    const angularVel = dt > 0 ? turned / dt : 0;

    if (angularVel > TURN_SPREAD_EPS) {
      me.spreadHalf = Math.min(
        MAX_SPREAD_HALF_RAD,
        me.spreadHalf + (MAX_SPREAD_HALF_RAD * dt) / ACCURACY_BUILD_S
      );
    } else if (me.spreadHalf > 0 && !this.hasAbility(ABILITY_SHOTGUN)) {
      // Shotgun never tightens — you live with the full cone
      me.spreadHalf = Math.max(
        0,
        me.spreadHalf - (MAX_SPREAD_HALF_RAD * dt) / ACCURACY_RECOVER_S
      );
    }
  }

  private updateDrawPoses(dt: number): void {
    const me = fighterFor(this.state, this.role);
    const them = fighterFor(this.state, otherRole(this.role));
    syncDrawPose(me);
    lerpDrawPose(them, dt);
  }

  private syncState(now: number): void {
    if (
      this.state.phase !== "maneuver" &&
      this.state.phase !== "aim" &&
      this.state.phase !== "settle"
    ) {
      return;
    }
    if (now - this.lastSyncMs < STATE_SYNC_MS) return;
    this.lastSyncMs = now;
    const me = fighterFor(this.state, this.role);
    try {
      this.session.sendPeer({
        type: "state",
        x: me.x,
        y: me.y,
        facing: me.facing,
        spreadHalf: me.spreadHalf,
        facingLeft: me.facingLeft,
        stepCount: me.stepCount,
        stepProgress: me.stepProgress,
        t: now,
        ...(this.role === "host"
          ? {
              tumbleweeds: this.tumbleweeds.snapshot(),
              jackrabbits: this.jackrabbits.snapshot(),
              snake: this.snake.snapshot(),
            }
          : {}),
      });
    } catch {
      /* channel may close */
    }
  }

  private updateSnake(dt: number): void {
    const hunting =
      this.state.phase === "maneuver" || this.state.phase === "aim";
    // Guest predicts locally; host authoritative bites + sync overwrite.
    const bitten = this.snake.update(
      dt,
      this.state.host,
      this.state.guest,
      hunting
    );
    if (bitten && this.role === "host") {
      this.applySnakeBite(bitten, true);
    }
  }

  private applySnakeBite(role: Role, notifyPeer: boolean): void {
    const f = fighterFor(this.state, role);
    if (f.hit) return;
    f.hit = true;
    playHiss();
    if (notifyPeer) {
      this.session.sendPeer({ type: "snake_bite", role });
    }
    if (this.role === "host") this.maybeResolve();
  }

  private killSnake(notifyPeer: boolean): void {
    if (!this.snake.snake) return;
    this.snake.kill();
    playHiss();
    if (notifyPeer) {
      this.session.sendPeer({ type: "snake_kill" });
    }
  }

  private spawnBullet(
    owner: Role,
    x: number,
    y: number,
    facing: number,
    opts?: { shotgun?: boolean; smoke?: boolean }
  ): void {
    const shotgun = opts?.shotgun === true;
    const smoke = opts?.smoke !== false;
    const speed = shotgun ? SHOTGUN_SPEED : BULLET_SPEED;
    const muzzle = 28;
    const mx = x + Math.cos(facing) * muzzle;
    const my = y + Math.sin(facing) * muzzle;
    const b: Bullet = {
      id: bulletId++,
      owner,
      x: mx,
      y: my,
      vx: Math.cos(facing) * speed,
      vy: Math.sin(facing) * speed,
      alive: true,
      shotgun,
    };
    this.state.bullets.push(b);
    if (smoke) this.dust.spawnMuzzleSmoke(mx, my, facing);
  }

  private updateBullets(dt: number): void {
    const host = this.state.host;
    const guest = this.state.guest;

    for (const b of this.state.bullets) {
      if (!b.alive) continue;
      const prevX = b.x;
      const prevY = b.y;
      b.x += b.vx * dt;
      b.y += b.vy * dt;

      // Dummy pops on contact but does not stop the shot.
      for (const dummy of [...this.state.dummies]) {
        const hit = segmentHitHitBox(prevX, prevY, b.x, b.y, dummy.x, dummy.y);
        if (hit) {
          this.popDummy(dummy.owner, true);
        }
      }

      // Snake dies when shot; shot keeps flying.
      const snake = this.snake.snake;
      if (snake && snake.alive) {
        const simg = getSprite("snake");
        const sw = simg
          ? Math.floor(simg.naturalWidth / SNAKE_FRAMES) * 0.5
          : 24;
        const sh = simg ? simg.naturalHeight * 0.5 : 12;
        const hit = segmentHitAabb(
          prevX,
          prevY,
          b.x,
          b.y,
          snake.x,
          snake.y,
          Math.max(12, sw),
          Math.max(10, sh)
        );
        if (hit) this.killSnake(true);
      }

      // Horse → two corpses; shot keeps flying.
      const horse = this.state.decor.find((p) => p.type === "horse");
      if (horse) {
        const img = getSprite("horse");
        const hw = (img?.naturalWidth || DECOR_SIZE) * 0.45;
        const hh = (img?.naturalHeight || DECOR_SIZE) * 0.45;
        const hit = segmentHitAabb(
          prevX,
          prevY,
          b.x,
          b.y,
          horse.x,
          horse.y - (img?.naturalHeight || DECOR_SIZE) / 2,
          hw,
          hh
        );
        if (hit) this.killHorse(true);
      }

      // Pellets always stop on contact; host alone owns the hit flag.
      if (b.owner === "host") {
        const hit = segmentHitHitBox(prevX, prevY, b.x, b.y, guest.x, guest.y);
        if (hit) {
          b.x = hit.x;
          b.y = hit.y;
          b.alive = false;
          if (!guest.hit && this.role === "host") {
            guest.hit = true;
            playHurt();
            this.session.sendPeer({ type: "hit", role: "guest" });
          }
          continue;
        }
      } else if (b.owner === "guest") {
        const hit = segmentHitHitBox(prevX, prevY, b.x, b.y, host.x, host.y);
        if (hit) {
          b.x = hit.x;
          b.y = hit.y;
          b.alive = false;
          if (!host.hit && this.role === "host") {
            host.hit = true;
            playHurt();
            this.session.sendPeer({ type: "hit", role: "host" });
          }
          continue;
        }
      }

      if (!pointInArena(b.x, b.y)) {
        b.alive = false;
      }
    }

    this.state.bullets = this.state.bullets.filter((b) => b.alive);
    if (this.role === "host") this.maybeResolve();
  }

  /** Burst a Cowboy Dummy — local collision notifies the peer. */
  private popDummy(owner: Role, notifyPeer: boolean): void {
    const idx = this.state.dummies.findIndex((d) => d.owner === owner);
    if (idx < 0) return;
    const dummy = this.state.dummies[idx]!;
    this.state.dummies.splice(idx, 1);
    this.dust.spawnDummyPop(dummy.x, dummy.y);
    playPop();
    if (notifyPeer) {
      this.session.sendPeer({ type: "dummy_pop", owner });
    }
  }

  /** Horse dies into a pair of player_dead sprites where it was. */
  private killHorse(notifyPeer: boolean): void {
    const idx = this.state.decor.findIndex((p) => p.type === "horse");
    if (idx < 0) return;
    const horse = this.state.decor[idx]!;
    this.state.decor.splice(idx, 1);
    this.state.horseBubbleText = null;
    this.state.horseBubbleUntilMs = 0;

    const spacing = Math.round(DEAD_W * 0.55);
    this.state.decor.push(
      {
        type: "dead",
        variant: 1,
        x: horse.x - spacing,
        y: horse.y,
      },
      {
        type: "dead",
        variant: 1,
        x: horse.x + spacing,
        y: horse.y,
      }
    );
    playHurt();
    window.setTimeout(() => playHurt(), 120);
    if (notifyPeer) {
      this.session.sendPeer({ type: "horse_kill" });
    }
  }

  private maybeResolve(force = false): void {
    if (this.resolved || this.role !== "host") return;
    if (this.state.phase !== "aim" && !force) return;

    // Hit before they could shoot — round is over
    const killedBeforeShot =
      (this.state.host.hit && !this.state.host.hasShot) ||
      (this.state.guest.hit && !this.state.guest.hasShot);
    if (killedBeforeShot) {
      this.resolve();
      return;
    }

    const bothShot = this.state.host.hasShot && this.state.guest.hasShot;
    if (!bothShot && !force) return;

    // Give in-flight bullets a moment unless forcing timeout
    if (!force && this.state.bullets.some((b) => b.alive)) return;

    this.resolve();
  }

  private resolve(): void {
    if (this.resolved || this.role !== "host") return;
    this.resolved = true;

    const hostHit = this.state.host.hit;
    const guestHit = this.state.guest.hit;
    let outcome: DuelOutcome;
    if (hostHit && !guestHit) outcome = "guest_win";
    else if (guestHit && !hostHit) outcome = "host_win";
    else outcome = "tie";

    const result = {
      type: "result" as const,
      outcome,
      hostHit,
      guestHit,
      hostShot: this.state.host.hasShot,
      guestShot: this.state.guest.hasShot,
    };
    this.applyResult(result);
    this.session.sendPeer(result);
  }

  private applyResult(msg: {
    outcome: DuelOutcome;
    hostHit: boolean;
    guestHit: boolean;
    hostShot: boolean;
    guestShot: boolean;
  }): void {
    this.resolved = true;
    this.state.phase = "result";
    this.state.fogActive = false;
    this.state.outcome = msg.outcome;
    this.state.host.hit = msg.hostHit;
    this.state.guest.hit = msg.guestHit;
    this.state.host.hasShot = msg.hostShot;
    this.state.guest.hasShot = msg.guestShot;
    this.state.bullets = [];

    stopCountdown();
    const won =
      (this.role === "host" && msg.outcome === "host_win") ||
      (this.role === "guest" && msg.outcome === "guest_win");
    playResultTheme(won);
  }

  private onPeer(msg: PeerMessage): void {
    switch (msg.type) {
      case "ready":
        if (this.state.phase === "result") {
          // Rematch flow: treat a ready while we're still on the result menu
          // as the peer already committing to the next round.
          if (msg.ready) {
            this.remoteRematchReady = true;
            if (msg.abilities) this.remoteAbilities = [...msg.abilities];
          }
          break;
        }
        this.state.remoteReady = msg.ready;
        if (msg.ready && msg.abilities) {
          this.remoteAbilities = [...msg.abilities];
        }
        this.maybeStartMatch();
        break;
      case "match_start": {
        if (this.role === "guest") {
          const localOrigin = performance.now();
          this.applyMatchStart(
            localOrigin,
            msg.hostStartMs,
            msg.settleMs,
            msg.maneuverMs,
            msg.aimMs,
            msg.decorSeed,
            msg.isNight ?? isNightArena(msg.decorSeed),
            msg.snakeCorner !== undefined
              ? msg.snakeCorner
              : maybeRollSnake(msg.decorSeed)
          );
          this.state.localReady = true;
          this.state.remoteReady = true;
        }
        break;
      }
      case "state": {
        const them = fighterFor(this.state, otherRole(this.role));
        them.x = msg.x;
        them.y = msg.y;
        them.facing = msg.facing;
        them.spreadHalf = msg.spreadHalf;
        them.facingLeft = msg.facingLeft;
        them.stepCount = msg.stepCount;
        them.stepProgress = msg.stepProgress;
        if (this.role === "guest") {
          if (msg.tumbleweeds) this.tumbleweeds.applyRemote(msg.tumbleweeds);
          if (msg.jackrabbits) this.jackrabbits.applyRemote(msg.jackrabbits);
          if (msg.snake !== undefined) this.snake.applyRemote(msg.snake);
        }
        break;
      }
      case "shoot": {
        const them = fighterFor(this.state, otherRole(this.role));
        if (them.hasShot || them.hit) break;
        them.hasShot = true;
        them.x = msg.x;
        them.y = msg.y;
        them.facing = msg.facing;
        const owner = otherRole(this.role);
        if (msg.pellets && msg.pellets.length > 0) {
          for (let i = 0; i < msg.pellets.length; i++) {
            this.spawnBullet(owner, msg.x, msg.y, msg.pellets[i]!, {
              shotgun: true,
              smoke: i === 0,
            });
          }
        } else {
          this.spawnBullet(owner, msg.x, msg.y, msg.facing);
        }
        playBang();
        if (this.role === "host") this.maybeResolve();
        break;
      }
      case "hit": {
        const f = fighterFor(this.state, msg.role);
        f.hit = true;
        playHurt();
        break;
      }
      case "result":
        this.applyResult(msg);
        break;
      case "emote": {
        const them = fighterFor(this.state, otherRole(this.role));
        this.showBubble(them, msg.text, performance.now());
        break;
      }
      case "dummy": {
        const owner = otherRole(this.role);
        if (this.state.dummies.some((d) => d.owner === owner)) break;
        this.state.dummies.push({
          owner,
          x: msg.x,
          y: msg.y,
          facingLeft: msg.facingLeft,
          placedAtMs: performance.now(),
        });
        break;
      }
      case "dummy_pop": {
        this.popDummy(msg.owner, false);
        break;
      }
      case "horse_kill": {
        this.killHorse(false);
        break;
      }
      case "snake_bite": {
        this.applySnakeBite(msg.role, false);
        break;
      }
      case "snake_kill": {
        this.killSnake(false);
        break;
      }
      case "rematch":
        // Peer rematched. If we're still on the result screen, remember it
        // until we rematch too — don't yank their rematch menu away.
        if (this.state.phase === "result") {
          this.remoteRematchReady = true;
        } else {
          this.state.remoteReady = true;
          this.maybeStartMatch();
        }
        break;
    }
  }

  /** Reset, mark ourselves ready, and skip Face Off. */
  requestRematch(): void {
    this.session.sendPeer({ type: "rematch" });
    const peerAlreadyReady = this.remoteRematchReady;
    const keptRemoteAbilities = peerAlreadyReady ? [...this.remoteAbilities] : [];
    this.remoteRematchReady = false;
    this.resetForRematch();
    if (peerAlreadyReady) {
      this.state.remoteReady = true;
      this.remoteAbilities = keptRemoteAbilities;
    }
    this.setLocalReady(true);
  }

  /** Whether the remote fighter should be drawn (fog of war). */
  isRemoteVisible(): boolean {
    if (!this.state.fogActive) return true;
    if (this.getSneakPeekReveal() > 0.02) return true;
    const them = fighterFor(this.state, otherRole(this.role));
    // Visible only if they crossed onto your half
    if (this.role === "host") return onHostHalf(them.x);
    return onGuestHalf(them.x);
  }
}

function turnToward(current: number, target: number, maxDelta: number): number {
  let diff = angleDiff(current, target);
  if (Math.abs(diff) <= maxDelta) return target;
  return current + Math.sign(diff) * maxDelta;
}

function angleDiff(from: number, to: number): number {
  let diff = to - from;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}
